import { useCallback, useState } from 'react';
import { startCase } from 'lodash';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import Page from 'components/layouts/page/Page';
import Button from 'components/shared/Button';
import CircularProgress from 'components/shared/CircularProgress';
import Menu from 'components/shared/menu';
import Tabs, { TabType } from 'components/Tabs';
import { useProfile } from 'context/profile';
import useChatConversations from 'hooks/useChatConversations';
import useNow from 'hooks/useNow';
import useWorkflow from 'hooks/useWorkflow';
import useWorkflowPermissions from 'hooks/useWorkflowPermissions';
import { simpleDateAtTime } from 'lib/date';
import { PERMISSIONS } from 'models/Profile';
import Workflow from 'models/Workflow';
import { ChatDraft } from 'services/api/chat';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { Body, BodySmall, BodySmallBold, H2 } from 'styles/typography';
import AttachFileIcon from 'svg/AttachFileIcon';
import BackArrowIcon from 'svg/BackArrowIcon';
import EllipsisIcon from 'svg/EllipsisIcon';
import NetworkProviderIcon from 'svg/NetworkProviderIcon';
import PatientIcon from 'svg/PatientIcon';
import TrashIcon from 'svg/TrashIcon';

import ChatTab, { CHANNEL_PARAM } from './chat/ChatTab';
import UnreadBadge from './chat/UnreadBadge';
import EligibilityCheckSlideout from './eligibility/EligibilityCheckSlideout';
import { TASKS_ROUTES } from './tasks/constants';
import ActivityLog from './ActivityLog';
import ArchiveReferralModal from './ArchiveReferralModal';
import CategoryCard from './CategoryCard';
import { INBOUND_REFERRALS_ROUTES, isPortalWorkflow, REFERRAL_INTAKE_API_NAME } from './constants';
import { elapsed } from './inboundReferralsTableColumns';
import PatientSummaryChips from './PatientSummaryChips';
import ProgressRail from './ProgressRail';
import ReferralModal, { ReferralModalSection } from './ReferralModal';
import ReferralSentTo from './ReferralSentTo';
import { WorkflowStatusBadge } from './StatusBadge';

// The design names the patient ("Referral for Ismael Dunn was successfully archived."), but
// `patientName` falls back to "New referral" before extraction has produced any demographics —
// and archiving a referral in exactly that state is a normal thing to do, so it would read
// "Referral for New referral was successfully archived." Name the patient only when there is one.
function archivedToast(workflow: Workflow): string {
  return workflow.hasPatientName
    ? `Referral for ${workflow.patientName} was successfully archived.`
    : 'Referral was successfully archived.';
}

// The page's standard tab strip (components/Tabs, same as the patient story page). Chat sits
// between Referral Review and Activity Log, matching the Figma tab order (OLIO-2832).
const PAGE_TABS: readonly TabType[] = [
  { label: 'Referral Review', value: 'review' },
  { label: 'Chat', value: 'chat' },
  { label: 'Activity Log', value: 'activity' },
];

export default function InboundReferralPage() {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { workflowId } = useParams<{ workflowId: string }>();
  const {
    workflow,
    isLoading,
    isError,
    completeActionAsync,
    isCompletingAction,
    amendActionAsync,
    isAmendingAction,
    saveActionProgress,
    isSavingActionProgress,
    invokeAction,
    isInvokingAction,
    dispatchActionAsync,
    resendActionAsync,
    isDispatchingAction,
    updateValuesAsync,
    markActionViewed,
    archiveWorkflow,
    isArchivingWorkflow,
  } = useWorkflow(workflowId);
  const { addToast } = useToastActions();
  // Only referral-intake workflows have chat — portal automation/discovery runs (also
  // rendered by this page) get no Chat tab, channel fetch, or subscription. chatMessageView
  // (QA PIQ 5 on OLIO-2837): roles without it get no tab and no fetch — the API 403s the
  // same users, so this hides a surface that could only error.
  const hasChat = workflow?.definition.apiName === REFERRAL_INTAKE_API_NAME && profile.has(PERMISSIONS.chatMessageView);
  // Mounted at page level so the Chat tab and the tab badge read one cache from any tab.
  const {
    conversations: chatConversations,
    isLoading: isChatLoading,
    isError: isChatError,
  } = useChatConversations(hasChat ? workflowId : undefined);
  // The active tab lives in the URL (?tab=chat) so it survives refresh and deep-links;
  // a ?channel param with no tab param implies the Chat tab. The default tab stays out
  // of the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') ?? (searchParams.has('channel') ? 'chat' : null);
  // Tab resolution runs over the visible set, so a ?tab=chat deep link on a portal run
  // falls back to the default tab.
  const availableTabs = hasChat ? PAGE_TABS : PAGE_TABS.filter((pageTab) => pageTab.value !== 'chat');
  const tab = availableTabs.find((pageTab) => pageTab.value === tabParam) ?? PAGE_TABS[0];
  // The actively-viewed channel (chat tab only) — excluded from the badge displays below.
  const channelParam = searchParams.get(CHANNEL_PARAM);
  const viewedChannelId = tab.value === 'chat' ? channelParam : null;
  // On the chat tab before ChatTab canonicalizes ?channel (tab entry writes ?tab=chat alone),
  // the rollup would count the about-to-be-viewed default channel — suppress the tab badge
  // for that render instead of guessing which channel ChatTab will resolve.
  const showTabBadge = tab.value !== 'chat' || channelParam !== null;
  // null = closed; otherwise the modal opens in edit mode focused on this section.
  const [editingSection, setEditingSection] = useState<ReferralModalSection | null>(null);
  // Unsent chat drafts, per conversation — page-owned so they survive tab switches (the
  // Chat tab unmounts on a trip to Referral Review or the Activity Log).
  const [chatDrafts, setChatDrafts] = useState<Record<string, ChatDraft>>({});
  const setChatDraft = useCallback(
    (conversationId: string, value: ChatDraft) => setChatDrafts((prev) => ({ ...prev, [conversationId]: value })),
    []
  );
  // Which coverage's benefit verification the slideout is showing (design 008 §3e) — undefined
  // when closed, null on a referral with no coverages. It's the coverage rather than a plain
  // boolean because the values behind the slideout belong to one coverage, so it has to know
  // which one it opened for.
  const [eligibilityFacetId, setEligibilityFacetId] = useState<string | null | undefined>(undefined);
  const setTab = (next: TabType) => {
    const params = new URLSearchParams(searchParams);
    // The default tab stays out of the URL — unless a lingering ?channel would re-imply
    // chat. The channel param is kept so returning to Chat restores the selection.
    if (next.value === PAGE_TABS[0].value && !params.has('channel')) params.delete('tab');
    else params.set('tab', next.value);
    setSearchParams(params);
  };
  const [confirmingArchive, setConfirmingArchive] = useState(false);
  const { canEdit } = useWorkflowPermissions();
  // The subline's "- N min ago" ticks on its own; server timestamps stay the source of truth.
  const now = useNow(30_000);

  // The Chat tab badge: the sum of unread counts across every visible facility except the
  // actively-viewed channel. Assumes the channel list is the complete visible set — if it
  // is ever paginated, derive this from the counts query instead (it stays whole-set).
  const totalUnread = chatConversations.reduce(
    (sum, conversation) => (conversation.id === viewedChannelId ? sum : sum + conversation.unreadCount),
    0
  );
  // Blue when any contributing channel's unread span mentions the viewer — the "you
  // specifically are wanted in Chat" signal, visible before the tab is opened.
  const anyMentioned = chatConversations.some(
    (conversation) => conversation.id !== viewedChannelId && conversation.unreadMentioned
  );
  const pageTabs = availableTabs.map((pageTab) =>
    pageTab.value === 'chat' && showTabBadge && totalUnread > 0
      ? { ...pageTab, affix: <UnreadBadge count={totalUnread} mentioned={anyMentioned} /> }
      : pageTab
  );

  if (isLoading)
    return (
      <Centered>
        <CircularProgress />
      </Centered>
    );
  if (isError || !workflow)
    return (
      <Centered>
        <Body>We couldn’t load this referral.</Body>
      </Centered>
    );

  // No in-app link lands a portal run here anymore, but the route still renders any run the
  // scope gate authorizes — and this page's portal accommodations are gone, so a direct URL
  // would get a blank title, an enabled edit menu, and an editable ReferralSentTo. Tasks is
  // where portal runs live now.
  if (isPortalWorkflow(workflow.definition.apiName)) return <Navigate to={TASKS_ROUTES.BASE} replace />;

  // Every affordance that opens ReferralModal goes through this one gate, and `openEditor` is
  // undefined when it's closed rather than a callback that no-ops: a component that can't be
  // handed an opener can't render a link that leads nowhere, so a new edit entry point has to
  // deal with the permission to compile (OLIO-2615). Portal runs redirected above, so no
  // per-type carve-out is needed here.
  const canEditReferral = canEdit && !workflow.isTerminal;
  const openEditor = canEditReferral ? setEditingSection : undefined;

  return (
    <Page>
      {editingSection && (
        <ReferralModal workflow={workflow} section={editingSection} onClose={() => setEditingSection(null)} />
      )}
      {confirmingArchive && (
        <ArchiveReferralModal
          onCancel={() => setConfirmingArchive(false)}
          isArchiving={isArchivingWorkflow}
          onConfirm={() =>
            archiveWorkflow(undefined, {
              onSuccess: () => {
                setConfirmingArchive(false);
                addToast({ text: archivedToast(workflow) });
              },
            })
          }
        />
      )}
      <EligibilityCheckSlideout
        open={eligibilityFacetId !== undefined}
        onClose={() => setEligibilityFacetId(undefined)}
        workflow={workflow}
        facetId={eligibilityFacetId ?? null}
        onComplete={(actionId, outputs) => completeActionAsync({ actionId, outputs }).catch(() => undefined)}
        isCompleting={isCompletingAction}
        onInvoke={(actionId, intent) => invokeAction({ actionId, invokeAction: intent })}
        isInvoking={isInvokingAction}
        onEditDetails={openEditor && (() => openEditor('demographics'))}
      />
      <Page.Header>
        <HeaderInner>
          <TitleRow>
            <BackButton type='button' onClick={() => navigate(INBOUND_REFERRALS_ROUTES.BASE)} aria-label='Back'>
              <BackArrowIcon width={20} height={20} />
            </BackButton>
            <H2>{workflow.patientName}</H2>
            <WorkflowStatusBadge status={workflow.status} archived={workflow.isArchived} />
            {/* startCase, not raw: the outcome is a slug, and since OLIO-2736 renamed it the
                underscore shows ("not_selected"). `capitalize` alone only fixed the first letter. */}
            {workflow.hasOutcome && (
              <OutcomeChip data-testid='workflow-outcome'>{startCase(workflow.outcome)}</OutcomeChip>
            )}
            {canEditReferral && (
              <MenuWrap>
                <Menu $size='large'>
                  <Menu.Trigger>
                    <Button
                      variant='outline-gray'
                      style={{ width: 44, height: 44, padding: 0, flexShrink: 0 }}
                      data-testid='referral-actions'>
                      <EllipsisIcon />
                    </Button>
                  </Menu.Trigger>
                  <Menu.Content position='right' style={{ marginTop: 4 }}>
                    {/* One entry per thing that can be edited, matching the in-card links and the
                        modal titles they open (UX, 2026-08-05). A single "Edit" gave no clue which
                        of the screens it led to — so each carries the icon for the thing it edits
                        rather than three identical pencils. */}
                    <EditMenuItem data-testid='edit-referral' onClick={() => setEditingSection('documents')}>
                      <AttachFileIcon width={11} height={14} color={colors.black100} />
                      Add or remove documents
                    </EditMenuItem>
                    <EditMenuItem
                      data-testid='edit-demographics-menu'
                      onClick={() => setEditingSection('demographics')}>
                      <PatientIcon width={14} height={14} color={colors.black100} />
                      Edit demographics
                    </EditMenuItem>
                    {/* Same screen the Referral Sent To card's Edit link opens, so it carries the
                        same icon the card does — the card is where it's discovered, the menu is
                        where someone looks for every edit. */}
                    <EditMenuItem data-testid='edit-facilities-menu' onClick={() => setEditingSection('facilities')}>
                      <NetworkProviderIcon width={14} height={14} color={colors.black100} />
                      Edit facilities
                    </EditMenuItem>
                    {/* Last and separated: everything above edits the referral, this one ends it. */}
                    <Menu.Divider />
                    <ArchiveMenuItem data-testid='archive-referral' onClick={() => setConfirmingArchive(true)}>
                      <TrashIcon width={14} height={14} color={colors.red100} />
                      Archive referral
                    </ArchiveMenuItem>
                  </Menu.Content>
                </Menu>
              </MenuWrap>
            )}
          </TitleRow>
          {workflow.createdAt && (
            <Subline>
              Created on {simpleDateAtTime(workflow.createdAt)}
              {workflow.createdByName && ` by ${workflow.createdByName}`} - {elapsed(workflow.createdAt, now)} ago
            </Subline>
          )}
          <PatientSummaryChips workflow={workflow} />
        </HeaderInner>
        <TabRow>
          <Tabs tabs={pageTabs} selectedTab={tab} onTabClick={setTab} />
        </TabRow>
      </Page.Header>
      <Page.Content>
        {tab.value === 'activity' && (
          <ActivityPane>
            <ActivityLog workflowId={workflow.id} isTerminal={workflow.isTerminal} />
          </ActivityPane>
        )}
        {tab.value === 'chat' && (
          <ChatTab
            conversations={chatConversations}
            isLoading={isChatLoading}
            isError={isChatError}
            drafts={chatDrafts}
            onDraftChange={setChatDraft}
          />
        )}
        {/* Hidden rather than unmounted: the decision panels hold in-progress local
            state (selections, notes) that a peek at the Activity Log must not wipe. */}
        <Layout style={{ display: tab.value === 'review' ? undefined : 'none' }}>
          <RailColumn>
            {/* Keyed by run: the route param can change without a remount, and the
                modal's open/search state must not leak across referrals. */}
            <ReferralSentTo key={workflow.id} workflow={workflow} editable={canEditReferral} />
            <ProgressRail workflow={workflow} />
          </RailColumn>

          <MainColumn>
            {/* onDispatch/onResend take the ASYNC variants, unlike the fire-and-forget writes beside
                them, because the sync modal awaits the outcome to decide whether to close. With
                `mutate` the promise resolved immediately and the modal closed even on a failed send —
                invisible while a refused dispatch still answered 200, and a lie the moment it
                answered 409. The modal catches and stays open; the toast carries the server's own
                message. */}
            {workflow.actionsByCategory().map(({ category, actions }) => (
              <CategoryCard
                key={category.key}
                category={category}
                actions={actions}
                workflow={workflow}
                onComplete={(actionId, outputs) => completeActionAsync({ actionId, outputs }).catch(() => undefined)}
                onAmend={(actionId, outputs) => amendActionAsync({ actionId, outputs }).catch(() => undefined)}
                isCompleting={isCompletingAction || isAmendingAction}
                onSaveProgress={(actionId, outputs, options) =>
                  saveActionProgress({ actionId, outputs, narrationHint: options?.narrationHint })
                }
                isSavingProgress={isSavingActionProgress}
                onInvoke={(actionId, intent) => invokeAction({ actionId, invokeAction: intent })}
                isInvoking={isInvokingAction}
                onDispatch={(actionId, outputs) => dispatchActionAsync({ actionId, outputs })}
                onResend={(action, outputs) => resendActionAsync({ action, outputs })}
                isDispatching={isDispatchingAction}
                onUpdateValues={updateValuesAsync}
                onView={(actionId) => markActionViewed({ actionId })}
                onEdit={openEditor}
                onOpenEligibility={setEligibilityFacetId}
              />
            ))}
          </MainColumn>
        </Layout>
      </Page.Content>
    </Page>
  );
}

const Centered = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const HeaderInner = styled.div`
  padding: 24px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MenuWrap = styled.div`
  margin-left: auto;
`;

const EditMenuItem = styled(Menu.Item)`
  gap: 12px;
`;

const ArchiveMenuItem = styled(EditMenuItem)`
  color: ${({ theme }) => theme.colors.red100};
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: ${colors.black100};
`;

const Subline = styled(BodySmall)`
  color: ${colors.black50};
`;

const OutcomeChip = styled(BodySmallBold)`
  text-transform: capitalize;
  padding: 3px 12px;
  border-radius: 24px;
  background: ${colors.blue10};
  color: ${colors.blue100};
`;

// The tabs band under the header content, styled like PatientHeader's: a
// hairline above, and the header's own border-bottom closing beneath the
// active tab's underline.
const TabRow = styled.div`
  border-top: 1px solid ${({ theme }) => theme.colors.black10};
  padding: 0 24px;
`;

const ActivityPane = styled.div`
  max-width: 760px;
`;

const Layout = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;

  @media (max-width: 900px) {
    flex-direction: column;
  }
`;

const RailColumn = styled.div`
  flex: 0 0 300px;
  max-width: 300px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  @media (max-width: 900px) {
    flex: 1 1 auto;
    max-width: none;
    width: 100%;
  }
`;

const MainColumn = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 720px;
`;
